class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.updateOptions();
    this.updateMasterId();
    this.highlightOptions();
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.highlightOptions(event);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }
  }

  /**
   * Update options array from both select dropdowns and radio buttons
   * Handles mixed button/dropdown scenarios by reading from both input types
   */
  updateOptions() {
    this.options = [];
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));

    // Find the radio and select options
    fieldsets.forEach((fieldset) => {
      const select = Array.from(fieldset.querySelectorAll('select')).find((select) => select.value);
      if (select) {
        this.options.push(select.value);
        return;
      }
      const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (radio) => radio.checked
      );
      if (radio) {
        this.options.push(radio.value);
        return;
      }
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          if (!this.options[index]) {
            return true;
          }
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // Update the price
        const mobileId = `price-mobile-${this.dataset.section}`;
        const desktopId = `price-desktop-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destinationMobile = document.getElementById(mobileId);
        const sourceMobile = html.getElementById(mobileId);
        const destinationDesktop = document.getElementById(desktopId);
        const sourceDesktop = html.getElementById(desktopId);

        if (sourceMobile && destinationMobile) {
          destinationMobile.innerHTML = sourceMobile.innerHTML;
        }
        if (sourceDesktop && destinationDesktop) {
          destinationDesktop.innerHTML = sourceDesktop.innerHTML;
        }

        // Update the add to cart button
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        }

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        if (inventoryQtySource && inventoryQtyDestination) {
          inventoryQtyDestination.value = inventoryQtySource.value;
        }

        // Update the product title
        const productTitleSource = html.querySelector('[data-product-title]');
        const productTitleDestination = document.querySelector('[data-product-title]');
        if (productTitleSource && productTitleDestination) {
          productTitleDestination.innerHTML = productTitleSource.innerHTML;
        }
      });
  }

  /**
   * toggleAddButton
   * @param {Boolean} disable
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    const addButtonText = addButton?.querySelector('[data-add-to-cart-button-text]');

    if (!addButton || !addButtonText) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || parseJson(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  /**
   * Check for each option value if the other one/two selected variant options are available
   * Only applies to radio button inputs
   * @param {Event} event - Optional event object
   */
  highlightOptions(event) {
    const optionGroups = this.querySelectorAll('[data-option-group]');
    if (optionGroups.length === 0) return;

    // If no variant is selected, enable all options
    if (!this.currentVariant) {
      optionGroups.forEach((option) => {
        const options = option.querySelectorAll('[data-option-value]');
        options.forEach((option) => {
          const label = option.closest('[data-option]').querySelector('[data-option-label]');
          label && label.classList.remove('option__label--disabled');
        });
      });
      return;
    }

    // If a variant is selected, loop over option groups
    optionGroups.forEach((option) => {
      // Get index from option group (0, 1 or 2)
      const optionIndex = parseInt(option.dataset.optionGroup);

      // Create compare options from group, remove selected option based on index
      const compareOptions = [...this.options].filter((option, index) => index != optionIndex);
      if (compareOptions.length === 0) return;

      // Loop over possible values for option group
      option.querySelectorAll('[data-option-value]').forEach((optionEl) => {
        const label =
          optionEl.closest('[data-option]')?.querySelector('[data-option-label]') || null;
        const value = optionEl.value;

        // Match option availability and check if the two current options are available for value
        let match =
          this.getVariantData().filter((variant) => {
            // Get available variants where the current option index is the option value
            if (variant.options[optionIndex] == value && variant.available) {
              // Create variant options, remove option based on option value
              const variantOptions = [...variant.options].filter(
                (option, index) => index != optionIndex
              );

              // Check if compare options is the same as variant options
              return variantOptions.every(
                (item, index) => item.toLowerCase() === compareOptions[index].toLowerCase()
              );
            }

            return false;
          }).length > 0;

        // Enable disable variant options
        label && label.classList.toggle('option__label--disabled', !match);
      });
    });
  }
}

// Register the unified component
if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
